import React, { useState } from 'react';
import {
  Field,
  Input,
  Tag,
  TagGroup,
  makeStyles,
  type TagGroupProps,
} from '@fluentui/react-components';

const NUMERIC = /^\d+$/;

const useStyles = makeStyles({
  tags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '6px',
  },
});

interface LocationIdsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** DOM id given to the underlying <Input> so a wrapping label can point at it. */
  inputId?: string;
  /** id of the field label — used to name the tag group for AT. */
  ariaLabelledBy?: string;
  placeholder?: string;
}

/**
 * Chip-style input for a list of numeric location IDs. Commits on comma,
 * space, Enter, or blur. Non-numeric tokens are rejected with an inline error;
 * duplicates are dropped. Each committed value renders as a dismissible Tag.
 */
export const LocationIdsInput: React.FC<LocationIdsInputProps> = ({
  value,
  onChange,
  inputId,
  ariaLabelledBy,
  placeholder = 'e.g. 12345, 67890',
}) => {
  const styles = useStyles();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const commit = (raw: string) => {
    const tokens = raw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (tokens.length === 0) {
      setText('');
      setError(null);
      return;
    }

    const valid: string[] = [];
    const invalid: string[] = [];
    for (const t of tokens) {
      (NUMERIC.test(t) ? valid : invalid).push(t);
    }

    if (valid.length > 0) {
      const seen = new Set(value);
      const merged = [...value];
      for (const id of valid) {
        if (!seen.has(id)) {
          seen.add(id);
          merged.push(id);
        }
      }
      onChange(merged);
    }

    setText('');
    setError(
      invalid.length > 0
        ? `Location IDs must be numeric — ignored: ${invalid.join(', ')}`
        : null,
    );
  };

  const handleDismiss: TagGroupProps['onDismiss'] = (_e, { value: v }) => {
    onChange(value.filter((id) => id !== v));
  };

  return (
    <>
      <Field
        validationState={error ? 'error' : undefined}
        validationMessage={error ?? undefined}
      >
        <Input
          id={inputId}
          value={text}
          onChange={(_e, data) => {
            const v = data.value;
            if (/[,\s]/.test(v)) commit(v);
            else setText(v);
          }}
          onBlur={() => commit(text)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(text);
            }
          }}
          placeholder={placeholder}
          style={{ width: '100%' }}
        />
      </Field>
      {value.length > 0 && (
        <TagGroup
          onDismiss={handleDismiss}
          aria-labelledby={ariaLabelledBy}
          className={styles.tags}
        >
          {value.map((id) => (
            <Tag
              key={id}
              value={id}
              dismissible
              dismissIcon={{ 'aria-label': `Remove ${id}` }}
            >
              {id}
            </Tag>
          ))}
        </TagGroup>
      )}
    </>
  );
};
